import { InputBlock } from '@sunfish/forms';
import { AuthMessageCard, AuthLayout } from '~/components/auth';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@sunfish/ui';
import { Button } from '@sunfish/ui';
import { Form, FormRootError } from '@sunfish/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link } from 'react-router';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { useTRPC } from '~utils/trpc/query-client';
import QueryClientProviders from '~/providers/query-client-provider';

const schema = z.object({
	email: z.string().email('Invalid email address')
});

export default function ForgotPasswordPage() {
	return (
		<QueryClientProviders>
			<ForgotPasswordContent />
		</QueryClientProviders>
	);
}

function ForgotPasswordContent() {
	const trpc = useTRPC();

	const form = useForm({
		resolver: zodResolver(schema),
		defaultValues: {
			email: ''
		}
	});

	const { mutateAsync } = trpc.auth.sendForgotPasswordEmail.useMutation();

	const onSubmit = async (data: z.output<typeof schema>) => {
		try {
			await mutateAsync({ email: data.email });
		} catch (err) {
			form.setError('root', {
				type: 'manual',
				message: 'Failed to send password reset link. Please try again.'
			});
		}
	};

	if (form.formState.isSubmitSuccessful) {
		return (
			<AuthLayout>
				<AuthMessageCard
					type="email"
					title="Check your email"
					message="We've sent a password reset link to your email address. Click the link in the email to reset your password."
					linkText="Back to login"
					linkHref="/login"
				/>
			</AuthLayout>
		);
	}

	return (
		<AuthLayout>
			<Form {...form}>
				<form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
					<Card className={'min-w-[500px]'}>
						<CardHeader>
							<CardTitle>Forgot your password?</CardTitle>
							<p className={'text-sm opacity-80 mt-1 -mb-2'}>
								Enter your email address and we'll send you a link to reset your password.
							</p>
						</CardHeader>
						<CardContent className="space-y-4">
							<FormRootError />
							<InputBlock
								name="email"
								label="Email"
								type="email"
								placeholder="Enter your email address"
								required
								disabled={form.formState.isSubmitting}
								control={form.control}
							/>
						</CardContent>
						<CardFooter>
							<div className="space-y-4 w-full">
								<Button
									type="submit"
									variant="brand"
									size="lg"
									className="w-full"
									disabled={form.formState.isSubmitting}
									onClick={form.handleSubmit(onSubmit)}
								>
									{form.formState.isSubmitting ? 'Sending...' : 'Send Reset Link'}
								</Button>
								<div className="text-center">
									<Link to="/login" className="text-sm font-medium text-muted-foreground hover:underline">
										Back to login
									</Link>
								</div>
							</div>
						</CardFooter>
					</Card>
				</form>
			</Form>
		</AuthLayout>
	);
}
